import { Link } from 'react-router-dom'
import { SEO, FAQSchema } from '@/components/seo'
import { ArrowRight, Shield, Sparkles, Eye, Check } from 'lucide-react'
import { Dropzone } from '@/components/dropzone/Dropzone'
import { buttonStyles } from '@/components/ui'
import { Stats, Testimonials, BeforeAfter } from '@/components/sections'

export function Undetectable() {
  const faqItems = [
    {
      question: 'How do you make AI images undetectable?',
      answer: 'We use multiple techniques: removing invisible watermarks (SynthID, Stable Signature), stripping metadata (C2PA, EXIF), and applying subtle modifications that break AI classifier patterns.',
    },
    {
      question: 'Will my images pass AI detectors after processing?',
      answer: 'Our tool significantly reduces detection rates on major platforms. However, no method is 100% guaranteed as detection technology evolves.',
    },
    {
      question: 'Does making images undetectable reduce quality?',
      answer: 'No, our algorithms are designed to preserve original image quality while removing detectable markers.',
    },
    {
      question: 'Is it legal to make AI images undetectable?',
      answer: 'Modifying your own images is legal. However, using them to deceive or violate platform policies may have consequences.',
    },
  ]

  return (
    <>
      <SEO
        title="Make AI Images Undetectable - Bypass AI Detection Free"
        description="Make your AI-generated images undetectable. Remove watermarks, metadata, and AI markers to bypass detection on any platform. Free tool."
        canonical="/undetectable"
        keywords="make ai images undetectable, undetectable ai images, bypass ai detection, ai image undetectable"
      />
      <FAQSchema questions={faqItems} />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-indigo-50 via-white to-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-sm text-indigo-700 font-medium mb-6">
              <Shield className="w-4 h-4" />
              AI Detection Bypass Tool
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Make AI Images{' '}
              <span className="text-indigo-600">Undetectable</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Remove all traces of AI generation from your images. Bypass detection on
              Instagram, Pinterest, Facebook, and other platforms.
            </p>
          </div>

          {/* Dropzone */}
          <div className="max-w-2xl mx-auto">
            <Dropzone onFileSelect={() => {}} />
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Removes SynthID & C2PA
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Strips all metadata
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Quality preserved
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Trusted By */}
      <Stats />

      {/* Before/After Examples */}
      <BeforeAfter />

      {/* Testimonials */}
      <Testimonials />

      {/* What We Remove */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Makes Images Detectable?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              AI images contain multiple layers of identifiable markers. We remove them all.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: 'SynthID Watermarks',
                description: 'Invisible watermarks from Google Gemini, Imagen, Nano Banana',
                removed: true,
              },
              {
                title: 'C2PA Metadata',
                description: 'Content Credentials from DALL-E, Firefly, Microsoft',
                removed: true,
              },
              {
                title: 'EXIF AI Tags',
                description: 'Software tags, generation parameters, tool signatures',
                removed: true,
              },
              {
                title: 'AI Patterns',
                description: 'Visual artifacts that classifiers detect',
                removed: true,
              },
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 relative">
                <div className="absolute top-4 right-4">
                  {item.removed && (
                    <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                      <Check className="w-4 h-4" /> Removed
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              How It Works
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: 'Upload Image',
                  description: 'Upload any AI-generated image',
                  icon: '📤',
                },
                {
                  step: '2',
                  title: 'Process',
                  description: 'We remove all detectable markers',
                  icon: '⚡',
                },
                {
                  step: '3',
                  title: 'Download',
                  description: 'Get your undetectable image',
                  icon: '✅',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Works With */}
      <section className="section bg-white">
        <div className="container">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Works With All AI Image Generators
          </h2>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {['Midjourney', 'DALL-E', 'ChatGPT', 'Gemini', 'Nano Banana', 'Stable Diffusion', 'Flux', 'Adobe Firefly', 'Leonardo', 'Ideogram', 'Grok', 'Microsoft Copilot'].map((name) => (
              <div key={name} className="px-4 py-2 bg-gray-100 rounded-lg text-gray-700 text-sm">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verify */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Eye className="w-12 h-12 text-primary-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Verify Your Results
            </h2>
            <p className="text-gray-600 mb-6">
              After processing, use our AI Detector to verify your image passes detection tests.
            </p>
            <Link to="/check" className={buttonStyles({ variant: 'secondary' })}>
              Check with AI Detector
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <details key={index} className="group bg-gray-50 rounded-xl">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="font-medium text-gray-900">{item.question}</span>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-gray-600">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-indigo-600">
        <div className="container text-center">
          <Sparkles className="w-10 h-10 text-white mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Make Your AI Images Undetectable
          </h2>
          <p className="text-indigo-100 mb-8 max-w-xl mx-auto">
            Free, instant, and quality-preserving. Start now.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-indigo-600 hover:bg-gray-100',
            })}
          >
            Process Image Free
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  )
}
